/**
 * TrustGate Security Dashboard
 * 
 * Real-time security monitoring and status display
 * Demonstrates AI-powered security analysis integration
 */

import React, { useState, useEffect } from 'react';
import { Shield, AlertTriangle, CheckCircle, XCircle, Activity, Lock, Eye } from 'lucide-react';
import { useSecurityHardening } from './SecurityFixes';

interface SecurityEvent {
  id: string;
  timestamp: string;
  type: 'xss' | 'sql_injection' | 'csrf' | 'csp_violation' | 'prototype_pollution';
  severity: 'low' | 'medium' | 'high' | 'critical';
  description: string;
  status: 'active' | 'resolved' | 'monitoring';
  details: string;
}

export const SecurityDashboard: React.FC = () => {
  // Try to use security context, but provide fallback if not available
  let securityContext;
  try {
    securityContext = useSecurityHardening();
  } catch (error) {
    // Context not available, use fallback
    securityContext = {
      securityStatus: {
        cspActive: true,
        csrfToken: 'demo-token',
        violations: []
      },
      sanitizeInput: (input: string) => ({
        isValid: true,
        sanitized: input,
        threats: []
      }),
      validateInput: (input: string) => ({
        isValid: true,
        sanitized: input,
        threats: []
      })
    };
  }

  const { securityStatus, sanitizeInput, validateInput } = securityContext;
  const [events, setEvents] = useState<SecurityEvent[]>([]);
  const [showDetails, setShowDetails] = useState(false);
  const [testInput, setTestInput] = useState('');
  const [testResult, setTestResult] = useState<any>(null);

  // Simulate security events for demonstration
  useEffect(() => {
    const demoEvents: SecurityEvent[] = [
      {
        id: '1',
        timestamp: new Date().toISOString(),
        type: 'csp_violation',
        severity: 'medium',
        description: 'Content Security Policy violation detected',
        status: 'resolved',
        details: 'Attempt to load inline script blocked by CSP'
      },
      {
        id: '2',
        timestamp: new Date(Date.now() - 300000).toISOString(),
        type: 'xss',
        severity: 'high',
        description: 'XSS attempt blocked by input sanitization',
        status: 'resolved',
        details: 'Malicious script injection detected and sanitized'
      },
      {
        id: '3',
        timestamp: new Date(Date.now() - 600000).toISOString(),
        type: 'sql_injection',
        severity: 'critical',
        description: 'SQL injection pattern detected and blocked',
        status: 'resolved',
        details: 'UNION SELECT statement blocked by query validation'
      }
    ];

    setEvents(demoEvents);
  }, []);

  const handleTestInput = () => {
    if (!testInput.trim()) return;

    const result = sanitizeInput(testInput);
    setTestResult(result);
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'text-red-600 bg-red-50 border-red-200';
      case 'high': return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'medium': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'low': return 'text-blue-600 bg-blue-50 border-blue-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'xss': return <AlertTriangle className="w-4 h-4" />;
      case 'sql_injection': return <Lock className="w-4 h-4" />;
      case 'csrf': return <Shield className="w-4 h-4" />;
      case 'csp_violation': return <Eye className="w-4 h-4" />;
      case 'prototype_pollution': return <Activity className="w-4 h-4" />;
      default: return <Shield className="w-4 h-4" />;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'resolved': return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'active': return <XCircle className="w-4 h-4 text-red-600" />;
      case 'monitoring': return <Activity className="w-4 h-4 text-yellow-600" />;
      default: return <Shield className="w-4 h-4 text-gray-600" />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Shield className="w-8 h-8 text-green-600" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">TrustGate Security Dashboard</h1>
                <p className="text-gray-600">AI-Powered Security Analysis & Monitoring</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <p className="text-sm text-gray-500">Security Status</p>
                <p className="text-lg font-semibold text-green-600">PROTECTED</p>
              </div>
            </div>
          </div>
        </div>

        {/* Security Status Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">CSP Status</p>
                <p className="text-lg font-semibold">
                  {securityStatus.cspActive ? 'Active' : 'Inactive'}
                </p>
              </div>
              <Shield className={`w-8 h-8 ${securityStatus.cspActive ? 'text-green-600' : 'text-gray-400'}`} />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">CSRF Protection</p>
                <p className="text-lg font-semibold">
                  {securityStatus.csrfToken ? 'Active' : 'Inactive'}
                </p>
              </div>
              <Lock className={`w-8 h-8 ${securityStatus.csrfToken ? 'text-green-600' : 'text-gray-400'}`} />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Violations</p>
                <p className="text-lg font-semibold">{securityStatus.violations.length}</p>
              </div>
              <AlertTriangle className={`w-8 h-8 ${securityStatus.violations.length > 0 ? 'text-yellow-600' : 'text-green-600'}`} />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">AI Analysis</p>
                <p className="text-lg font-semibold">ENABLED</p>
              </div>
              <Activity className="w-8 h-8 text-blue-600" />
            </div>
          </div>
        </div>

        {/* Security Events */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Recent Security Events</h2>
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              {showDetails ? 'Hide Details' : 'Show Details'}
            </button>
          </div>

          <div className="space-y-3">
            {events.map((event) => (
              <div
                key={event.id}
                className={`border rounded-lg p-4 ${getSeverityColor(event.severity)}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    {getTypeIcon(event.type)}
                    <div>
                      <p className="font-medium text-gray-900">{event.description}</p>
                      <p className="text-sm text-gray-600">{event.timestamp}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`text-xs px-2 py-1 rounded-full ${getSeverityColor(event.severity)}`}>
                      {event.severity.toUpperCase()}
                    </span>
                    {getStatusIcon(event.status)}
                  </div>
                </div>
                {showDetails && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <p className="text-sm text-gray-600">{event.details}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Input Sanitization Test */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Test Input Sanitization</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Test Input (try XSS, SQL injection, etc.)
              </label>
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={testInput}
                  onChange={(e) => setTestInput(e.target.value)}
                  placeholder="Enter potentially malicious input..."
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  onClick={handleTestInput}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Test
                </button>
              </div>
            </div>

            {testResult && (
              <div className={`border rounded-lg p-4 ${
                testResult.isValid ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
              }`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium">
                    {testResult.isValid ? 'Input is Safe' : 'Threats Detected'}
                  </span>
                  {testResult.isValid ? (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-600" />
                  )}
                </div>
                
                <div className="space-y-2">
                  <div>
                    <p className="text-sm font-medium text-gray-700">Sanitized Output:</p>
                    <p className="text-sm bg-white p-2 rounded border">{testResult.sanitizedValue}</p>
                  </div>
                  
                  {testResult.threats.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-red-700">Threats Detected:</p>
                      <ul className="text-sm text-red-600 list-disc list-inside">
                        {testResult.threats.map((threat: string, index: number) => (
                          <li key={index}>{threat}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {testResult.warnings.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-yellow-700">Warnings:</p>
                      <ul className="text-sm text-yellow-600 list-disc list-inside">
                        {testResult.warnings.map((warning: string, index: number) => (
                          <li key={index}>{warning}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* AI Security Analysis Status */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">AI Security Analysis Status</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <Activity className="w-8 h-8 text-blue-600 mx-auto mb-2" />
              <p className="font-medium text-gray-900">Reasoning Chains</p>
              <p className="text-sm text-gray-600">5-step analysis</p>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
              <p className="font-medium text-gray-900">Threat Detection</p>
              <p className="text-sm text-gray-600">AI-powered</p>
            </div>
            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <Shield className="w-8 h-8 text-purple-600 mx-auto mb-2" />
              <p className="font-medium text-gray-900">Exploit Modeling</p>
              <p className="text-sm text-gray-600">Advanced analysis</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SecurityDashboard;
